import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import AOS from "aos";

// Hooks
import useAPI from "../hooks/useAPI";

// Utils
import { isValidName, isAllDigits, isValidEmail } from "../utils/validation";
import { contact_us } from "../utils/data/contact-us";
import { getFormattedTimestamp } from "../utils/formatters";

// Material UI Components
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Zoom,
  InputAdornment,
  TextField,
  CircularProgress,
} from "@mui/material";

// Material UI Icons
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3667.4973993967506!2d72.62634057531884!3d23.18853697905781!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395c2a3c9618d2c5%3A0xc54de484f986b1fa!2sDA-IICT!5e0!3m2!1sen!2sin!4v1735023673652!5m2!1sen!2sin";

function ContactUs() {
  const { POST } = useAPI();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [message, setMessage] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isJustVerify, setIsJustVerify] = useState(false);

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (isValidName(value) && value.length < 256) {
      //allow only digits a-zA-Z[space]
      setName(value);
    }
  };

  const handleEmailChange = (e) => {
    if (e.target.value.length < 256) {
      setEmail(e.target.value);
    }
  };

  const handleContactNumberChange = (e) => {
    if (e.target.value.length <= 3) return;

    let value = e.target.value.replace("+91 ", "").trim();
    value = value.replace(" ", "").trim();

    // Allow only digits
    if (isAllDigits(value) && value.length <= 10 && value[0] !== "0") {
      //allow only digits
      value =
        value.substring(0, 5) +
        (value.length > 5 ? " " + value.substring(5) : "");
      setContactNumber(value);
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const isFromValid = () => {
    return (
      name.trim() !== "" &&
      isValidEmail(email) &&
      contactNumber.length === 11 &&
      message.trim() !== ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsJustVerify(true);

    if (!isFromValid()) return;

    const data = {
      name: name.trim(),
      email,
      contactNumber,
      message: message.trim(),
    };

    const sheetsData = {
      spreadsheetId: "1sP7os6sXpY9KGrVRfmc_RonN4NMq2Il4azFm7LzHTtc",
      sheetName: "Sheet1",
      data: [
        name.trim(),
        email,
        contactNumber,
        message.trim(),
        getFormattedTimestamp(),
      ],
    };

    setIsLoading((prev) => true);

    try {
      await POST("/data-services/store-data", sheetsData);
      await POST("/mail-services/send-mail/contact-us", data);

      toast("Submitted Successfully", {
        icon: <CheckCircleRoundedIcon />,
        style: {
          borderRadius: "10px",
          background: "white",
          color: "black",
        },
      });

      setIsJustVerify((prev) => false);
      setName((prev) => "");
      setEmail((prev) => "");
      setContactNumber((prev) => "");
      setMessage((prev) => "");
    } catch (error) {
      toast(error.response?.data?.message || "Submission Unsuccessful", {
        icon: <CancelRoundedIcon />,
        style: {
          borderRadius: "10px",
          background: "white",
          color: "black",
        },
      });
    } finally {
      setIsLoading((prev) => false);
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  return (
    <Box data-aos="fade-up" sx={{ p: 1, mb: 10 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 4,
        }}
      >
        <Typography variant="h4" color="white" fontWeight="bold">
          Contact Us
        </Typography>
      </Box>
      <Grid
        container
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          padding={0}
          margin={0}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 520,
              px: 2,
              backdropFilter: "blur(6px)",
              borderRadius: 3,
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <TextField
                value={name}
                onChange={handleNameChange}
                name="name"
                id="name"
                label="Name"
                placeholder="e.g. manoj"
                variant="outlined"
                fullWidth
                required
                size="small"
                autoComplete="on"
                error={isJustVerify && name.trim() === ""}
                helperText={
                  isJustVerify &&
                  (name.trim() === "" ? "Name cannot be empty" : "")
                }
                FormHelperTextProps={{
                  sx: {
                    color: "red", // Change the helper text color
                    fontWeight: "bold",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#333333" }} />
                    </InputAdornment>
                  ),
                  endAdornment:
                    isJustVerify && name.trim() === "" ? (
                      <InputAdornment position="end">
                        <Tooltip
                          TransitionComponent={Zoom}
                          title={
                            name.trim() === "" ? "Name cannot be empty" : ""
                          }
                          placement="bottom"
                          arrow
                          leaveDelay={0}
                          enterDelay={0}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid black",
                                bgcolor: "white",
                                color: "black",
                                transition: "none",
                                fontWeight: "bold",
                              },
                            },
                            arrow: {
                              sx: {
                                color: "black",
                              },
                            },
                          }}
                        >
                          <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                }}
                inputProps={{
                  required: false, // disables the default pop-up message
                }}
                sx={{
                  color: "white", // Set default text color to white
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontWeight: "bold",
                    color: "white", // Text color inside the input is white
                    borderColor: "white", // Set default border color to white
                    "& .MuiInputBase-input": {
                      color: "white", // Set text color inside the input field to white
                      borderColor: "white",
                      fontWeight: "bold",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white", // Keep the border color white when focused
                      color: "white",
                      fontWeight: "bold",
                    },
                    "& fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Default border color for the fieldset
                    },
                    "&:hover fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Border color when hovered
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "white", // Change label color to white
                    fontWeight: "bold",
                    "&.Mui-focused": {
                      color: "white", // Keep label color white when focused
                      fontWeight: "bold",
                    },
                  },
                  "& .MuiInputAdornment-root": {
                    color: "white", // Set adornment color to white
                    fontWeight: "bold",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white", // Set icons color to white
                    fontWeight: "bold",
                  },
                }}
              />
              <TextField
                value={email}
                onChange={handleEmailChange}
                name="email"
                id="email"
                label="Email Id"
                placeholder="e.g. manoj@gmail.com"
                variant="outlined"
                fullWidth
                required
                size="small"
                autoComplete="on"
                error={isJustVerify && !isValidEmail(email)}
                helperText={
                  isJustVerify &&
                  (email.trim() === ""
                    ? "Email id cannot be empty"
                    : !isValidEmail(email)
                    ? "Email address is not valid"
                    : "")
                }
                FormHelperTextProps={{
                  sx: {
                    color: "red", // Change the helper text color
                    fontWeight: "bold",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon sx={{ color: "#333333" }} />
                    </InputAdornment>
                  ),
                  endAdornment:
                    isJustVerify && !isValidEmail(email) ? (
                      <InputAdornment position="end">
                        <Tooltip
                          TransitionComponent={Zoom}
                          title={
                            email.trim() === ""
                              ? "Email id cannot be empty"
                              : "Email address is not valid"
                          }
                          placement="bottom"
                          arrow
                          leaveDelay={0}
                          enterDelay={0}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid black",
                                bgcolor: "white",
                                color: "black",
                                transition: "none",
                                fontWeight: "bold",
                              },
                            },
                            arrow: {
                              sx: {
                                color: "black",
                              },
                            },
                          }}
                        >
                          <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                }}
                inputProps={{
                  required: false, // disables the default pop-up message
                }}
                sx={{
                  color: "white", // Set default text color to white
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontWeight: "bold",
                    color: "white", // Text color inside the input is white
                    borderColor: "white", // Set default border color to white
                    "& .MuiInputBase-input": {
                      color: "white", // Set text color inside the input field to white
                      borderColor: "white",
                      fontWeight: "bold",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white", // Keep the border color white when focused
                      color: "white",
                      fontWeight: "bold",
                    },
                    "& fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Default border color for the fieldset
                    },
                    "&:hover fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Border color when hovered
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "white", // Change label color to white
                    fontWeight: "bold",
                    "&.Mui-focused": {
                      color: "white", // Keep label color white when focused
                      fontWeight: "bold",
                    },
                  },
                  "& .MuiInputAdornment-root": {
                    color: "white", // Set adornment color to white
                    fontWeight: "bold",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white", // Set icons color to white
                    fontWeight: "bold",
                  },
                }}
              />
              <TextField
                value={`+91 ${contactNumber}`}
                onChange={handleContactNumberChange}
                name="contact-number"
                id="contact-number"
                label="Contact Number"
                placeholder="e.g. +91 99099 09909"
                variant="outlined"
                fullWidth
                required
                size="small"
                autoComplete="on"
                error={isJustVerify && contactNumber.length !== 11}
                helperText={
                  isJustVerify &&
                  (contactNumber === ""
                    ? "Contact number cannot be empty"
                    : contactNumber.length !== 11
                    ? "Please, Enter valid contact number"
                    : "")
                }
                FormHelperTextProps={{
                  sx: {
                    color: "red", // Change the helper text color
                    fontWeight: "bold",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CallRoundedIcon sx={{ color: "#333333" }} />
                    </InputAdornment>
                  ),
                  endAdornment:
                    isJustVerify && contactNumber.length !== 11 ? (
                      <InputAdornment position="end">
                        <Tooltip
                          TransitionComponent={Zoom}
                          title={
                            contactNumber === ""
                              ? "Phone number cannot be empty"
                              : contactNumber.length < 11
                              ? "Please, Enter valid phone number"
                              : ""
                          }
                          placement="bottom"
                          arrow
                          leaveDelay={0}
                          enterDelay={0}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid black",
                                bgcolor: "white",
                                color: "black",
                                transition: "none",
                                fontWeight: "bold",
                              },
                            },
                            arrow: {
                              sx: {
                                color: "black",
                              },
                            },
                          }}
                        >
                          <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                }}
                inputProps={{
                  required: false, // disables the default pop-up message
                }}
                sx={{
                  color: "white", // Set default text color to white
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontWeight: "bold",
                    color: "white", // Text color inside the input is white
                    borderColor: "white", // Set default border color to white
                    "& .MuiInputBase-input": {
                      color: "white", // Set text color inside the input field to white
                      borderColor: "white",
                      fontWeight: "bold",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white", // Keep the border color white when focused
                      color: "white",
                      fontWeight: "bold",
                    },
                    "& fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Default border color for the fieldset
                    },
                    "&:hover fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Border color when hovered
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "white", // Change label color to white
                    fontWeight: "bold",
                    "&.Mui-focused": {
                      color: "white", // Keep label color white when focused
                      fontWeight: "bold",
                    },
                  },
                  "& .MuiInputAdornment-root": {
                    color: "white", // Set adornment color to white
                    fontWeight: "bold",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white", // Set icons color to white
                    fontWeight: "bold",
                  },
                }}
              />
              <TextField
                value={message}
                onChange={handleMessageChange}
                name="message"
                id="message"
                label="Message"
                placeholder="Drop something here... :)"
                variant="outlined"
                fullWidth
                multiline
                minRows={4}
                maxRows={6}
                size="small"
                autoComplete="on"
                inputProps={{
                  required: false, // disables the default pop-up message
                }}
                error={isJustVerify && message.trim() === ""}
                helperText={
                  isJustVerify && message.trim() === ""
                    ? "Message cannot be empty"
                    : ""
                }
                FormHelperTextProps={{
                  sx: {
                    color: "red", // Change the helper text color
                    fontWeight: "bold",
                  },
                }}
                InputProps={{
                  endAdornment:
                    isJustVerify && message.trim() === "" ? (
                      <InputAdornment position="end">
                        <Tooltip
                          TransitionComponent={Zoom}
                          title={"Message cannot be empty"}
                          placement="bottom"
                          arrow
                          leaveDelay={0}
                          enterDelay={0}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid black",
                                bgcolor: "white",
                                color: "black",
                                transition: "none",
                                fontWeight: "bold",
                              },
                            },
                            arrow: {
                              sx: {
                                color: "black",
                              },
                            },
                          }}
                        >
                          <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                }}
                sx={{
                  color: "white", // Set default text color to white
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    fontWeight: "bold",
                    color: "white", // Text color inside the input is white
                    borderColor: "white", // Set default border color to white
                    "& .MuiInputBase-input": {
                      color: "white", // Set text color inside the input field to white
                      borderColor: "white",
                      fontWeight: "bold",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white", // Keep the border color white when focused
                      color: "white",
                      fontWeight: "bold",
                    },
                    "& fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Default border color for the fieldset
                    },
                    "&:hover fieldset": {
                      fontWeight: "bold",
                      borderColor: "white", // Border color when hovered
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "white", // Change label color to white
                    fontWeight: "bold",
                    "&.Mui-focused": {
                      color: "white", // Keep label color white when focused
                      fontWeight: "bold",
                    },
                  },
                  "& .MuiInputAdornment-root": {
                    color: "white", // Set adornment color to white
                    fontWeight: "bold",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white", // Set icons color to white
                    fontWeight: "bold",
                  },
                }}
              />
              <button
                type="submit"
                name="submit"
                style={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  backgroundColor: "white",
                  color: "black",
                  borderColor: "white",
                }}
              >
                {isLoading ? "Submitting... " : "Submit"}
                {isLoading ? (
                  <CircularProgress
                    size={20}
                    thickness={6}
                    sx={{
                      mx: 2,
                      color: "black",
                      "& circle": { strokeLinecap: "round" },
                    }}
                  />
                ) : null}
              </button>
            </form>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "row",
                gap: 1,
                bgcolor: "lavender",
                p: 1,
                my: 2,
                borderRadius: 3,
                bgcolor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <IconButton
                sx={{
                  width: 40, // Equal width
                  height: 40, // Equal height
                  borderRadius: "50%", // Makes it a perfect circle
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                }}
                onClick={() => window.open(contact_us.github, "_blank")}
              >
                <i
                  className="fa-brands fa-github"
                  style={{ color: "white" }}
                ></i>
              </IconButton>
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                }}
                onClick={() => window.open(contact_us.linkedin, "_blank")}
              >
                <i
                  className="fa-brands fa-linkedin"
                  style={{ color: "white" }}
                ></i>
              </IconButton>
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                }}
                onClick={() => window.open(contact_us.youtube, "_blank")}
              >
                <i
                  className="fa-brands fa-youtube"
                  style={{ color: "white" }}
                ></i>
              </IconButton>
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                }}
                onClick={() => window.open(contact_us.instagram, "_blank")}
              >
                <i
                  className="fa-brands fa-instagram"
                  style={{ color: "white" }}
                ></i>
              </IconButton>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          padding={0}
          margin={0}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              paddingTop: "56.25%", // 16:9 aspect ratio
              borderRadius: 4,
              overflow: "hidden",
              maxWidth: "100%",
            }}
          >
            <iframe
              src={GOOGLE_MAPS_URL}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ContactUs;
